import { isMobile } from '@/helpers/is-from-app';

const CURRENCY = 'TWD';

const SEND_TO_APP_FUNCTION = Object.freeze({
  PAGE_CONFIRMATION: 'sendDataToApp',
  CART_ITEM_REMOVED: 'sendItemRemovedToApp',
  CART_CLEARED: 'sendCartClearedToApp',
});

const debugGA4Event = (eventName, orderId, parameterForGA) => {
  try {
    gtag('event', eventName, {
      insider_orderID: orderId,
      insider_tracking: JSON.stringify(parameterForGA).substring(0, 100),
    });
  } catch (err) {
    console.log(`sent GA4 event error:${err}`);
  }
};

const reinitialize = (orderId = '') => {
  if (typeof Insider === 'object') {
    debugGA4Event('insider_44_reinitialize', orderId, {});
    Insider.eventManager.dispatch('init-manager:re-initialize');
    return;
  }
  debugGA4Event('insider_45_append_script', orderId, { check: process.env.VUE_APP_INSIDER_SCRIPT_SRC });
  const insiderScript = document.createElement('script');
  insiderScript.type = 'text/javascript';
  insiderScript.async = true;
  insiderScript.src = process.env.VUE_APP_INSIDER_SCRIPT_SRC;
  const fs = document.getElementsByTagName('script')[0];
  fs.parentNode.insertBefore(insiderScript, fs);
};

const sendInitializeHit = () => {
  if (typeof Insider === 'object' && typeof Insider.initializeHitAPI === 'function') {
    Insider.initializeHitAPI();
  }
};

const detectAppWebviewOS = () => {
  const browserAgent = String(window.navigator.userAgent).toLowerCase();
  let browserOS = '';
  let isWebview = false;
  if (/android/.test(browserAgent)) {
    browserOS = 'android';
    isWebview = / wv/.test(browserAgent);
  } else if (/iphone|ipod|ipad/.test(browserAgent)) {
    browserOS = 'ios';
    isWebview = !/safari/.test(browserAgent);
  }
  return browserOS !== '' && (isWebview || /webview/.test(browserAgent)) ? browserOS : '';
};

const makeAppWebviewPageConfirmation = (orderId, details) => {
  return details.map((detail) => {
    return {
      saleID: orderId,
      product: {
        name: detail.productName,
        productID: detail.martCode,
        imageURL: detail.masterImage || '',
        currency: CURRENCY,
        taxonomy: Object.values(detail.categories),
        custom: { product_no: [detail.productNo] },
        price: Number(detail.finalPrice),
        quantity: Number(detail.productQty),
      },
    };
  });
};

const sendWebBrowserPageConfirmation = (orderId) => {
  window.insider_object = window.insider_object || {};
  window.insider_object.transaction = {
    order_id: orderId,
  };

  window.insider_object.page = {
    type: 'Confirmation',
  };

  const ajaxListenerConfirmation = function (callback) {
    var oldOpen = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.addEventListener('readystatechange', function () {
        if (
          this.responseType !== 'arraybuffer' &&
          this.responseType !== 'blob' &&
          this.readyState === 4 &&
          this.status === 200 &&
          typeof callback === 'function'
        ) {
          callback(url, this.response, method);
        }
      });

      oldOpen.apply(this, arguments);
    };
  };

  ajaxListenerConfirmation(function (url, response, method) {
    if (
      method.toLowerCase() === 'post' &&
      url.indexOf('/hit') > -1 &&
      ((window.insider_object || {}).page || {}).type === 'Confirmation'
    ) {
      setTimeout(function () {
        delete (window.insider_object || {}).page;
      }, 2000);
    }
  });

  debugGA4Event('insider_13_purchase_web', orderId, {
    check: typeof Insider,
    dataTo: Object.keys(window.insider_object || {}),
    browser: window.navigator.userAgent || '',
  });

  reinitialize(orderId);
};

/**
 * 建立 parameter for Insider's basket object line_items properties.
 * @param {Array} cartDetail
 * @return {Array}
 * */
export const createBasketObject = (cartDetail) => {
  return cartDetail.map((item) => {
    return {
      product: {
        id: item.martCode,
        name: item.productName,
        taxonomy: Object.values(item.categories),
        currency: CURRENCY,
        url: isMobile() ? `${process.env.VUE_APP_ONLINE}${item.mbLink}` : `${process.env.VUE_APP_ONLINE}${item.pcLink}`,
        product_image_url: item.masterImage,
        unit_price: item.unitPrice,
        unit_sale_price: item.finalPrice,
        custom: {
          product_no: [item.productNo],
        },
      },
      quantity: Number(item.productQty),
      subtotal: item.finalPrice * item.productQty,
    };
  });
};

/**
 * 送出 Insider's Basket Object event
 * @param {Object} basketParameterForInsider
 * doc: https://academy.useinsider.com/docs/insider-object-integration-basket-object
 */
export const sendBasketObject = (cartAmount, cartDetail) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }
  window.insider_object = window.insider_object || {};
  try {
    const lineItemsForInsider = createBasketObject(cartDetail);
    const basketParameterForInsider = {
      currency: CURRENCY,
      total: Number(cartAmount),
      line_items: lineItemsForInsider,
    };
    // console.log(basketParameterForInsider);
    window.insider_object.basket = basketParameterForInsider;
    sendInitializeHit();
  } catch (err) {
    console.log('sent Insider basket object error');
    console.log(err);
  }
};

const isSendAppPostMessage = (appFunctionName, sendJson, ga4Event) => {
  try {
    const sendApp = typeof sendJson === 'string' ? sendJson : JSON.stringify(sendJson);
    switch (detectAppWebviewOS()) {
      case 'android':
        if (ga4Event !== null && typeof ga4Event === 'object') {
          debugGA4Event('insider_11_purchase_android', ga4Event.orderId || '', {
            dataTo: ga4Event.dataTo || '',
            check: typeof app[appFunctionName],
            browser: window.navigator.userAgent || '',
          });
        }
        if (app[appFunctionName]) {
          app[appFunctionName](sendApp);
        }
        break;
      case 'ios':
        if (ga4Event !== null && typeof ga4Event === 'object') {
          debugGA4Event('insider_12_purchase_ios', ga4Event.orderId || '', {
            dataTo: ga4Event.dataTo || '',
            check: typeof window.webkit.messageHandlers[appFunctionName],
            browser: window.navigator.userAgent || '',
          });
        }
        if (window.webkit.messageHandlers[appFunctionName]) {
          window.webkit.messageHandlers[appFunctionName].postMessage(sendApp);
        }
        break;
      default:
        return false;
    }
  } catch (e) {
    const rawJson = JSON.stringify(sendJson);
    console.log(`sent Insider ${appFunctionName} error : ${rawJson}`);
    console.log(e);
  }
  return true;
};

export const sendItemRemovedToApp = (martId) => {
  isSendAppPostMessage(SEND_TO_APP_FUNCTION.CART_ITEM_REMOVED, { productID: martId }, null);
};

export const sendCartClearedToApp = () => {
  isSendAppPostMessage(SEND_TO_APP_FUNCTION.CART_CLEARED, '', null);
};

export const sendPageConfirmation = (orderId, details) => {
  const sendJson = makeAppWebviewPageConfirmation(orderId, details);
  const ga4Event = { orderId: orderId, dataTo: 'details[' + String(details.length || 0) + ']' };
  try {
    if (isSendAppPostMessage(SEND_TO_APP_FUNCTION.PAGE_CONFIRMATION, sendJson, ga4Event)) {
      details.forEach((detail) => sendItemRemovedToApp(detail.martCode));
      sendCartClearedToApp();
      reinitialize(orderId);
      debugGA4Event('insider_88_purchase_sent', orderId, {});
      return;
    }
    sendWebBrowserPageConfirmation(orderId);
    debugGA4Event('insider_88_purchase_sent', orderId, {});
  } catch (e) {
    debugGA4Event('insider_99_purchase_error', orderId, { message: e.message || '', stack: e.stack || '' });
    console.log(`sent Insider page confirmation error: ${orderId}`);
    console.log(e);
  }
};
